import { fromPromise } from 'xstate';
import {
  AuthenticateSuccessEvent,
  AuthMachineContext,
  AuthMachineStatusCode,
} from '../types';
import { AuthTransport, resolveAuthHeaders } from '../transport';
import { request } from '../../helpers/request';
import { isDevelopment } from '../../helpers/env';

export type AuthenticateInput = {
  endpoint: {
    client: AuthMachineContext['endpoints']['login'];
  };
  password: string;
  /** Host-supplied header injection — see `../transport`. */
  transport?: AuthTransport;
  username: string;
};

export type AuthenticateResponse = {
  success: boolean;
  message?: string;
  /**
   * Structured failure code (an {@link AuthMachineStatusCode} value, e.g.
   * `ACCOUNT_LOCKED` for a temporary bad-password lockout). This — never the
   * display `message` — is what the actor maps to a status code: messages are
   * backend/Magento display copy and not a contract (CTAP-1197).
   */
  code?: string;
  access_token?: string;
};

// export type AuthenticateTokenOutput = {
//   type: string;
//   accessToken: string;
// };

/** @todo pass locale for auth server? */
export const authenticate = fromPromise<
  AuthenticateSuccessEvent,
  AuthenticateInput
>(async ({ input }) => {
  if (!input.endpoint.client || input.endpoint.client === '') {
    throw new Error('No client endpoint provided.');
  }

  /** @todo do we need to handle different status codes here...like what if the service is down? */
  try {
    const data = await request<AuthenticateResponse>(input.endpoint.client, {
      method: 'POST',
      mode: 'cors',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...resolveAuthHeaders(input.transport, 'login'),
      },
      body: JSON.stringify({
        username: input.username,
        password: input.password,
      }).toString(),
    });

    if (!data.success) {
      // Map a structured failure code verbatim when it's one we know —
      // NEVER the display message (that's backend/Magento copy, not a
      // contract). Unknown/absent codes collapse below.
      if (
        data.code &&
        (Object.values(AuthMachineStatusCode) as string[]).includes(data.code)
      ) {
        throw new Error(data.code);
      }

      throw new Error(
        data.message?.toString() ?? 'Sign in failed. Please try again.',
      );
    }

    return {
      type: 'AUTHENTICATE_SUCCESS',
      // The signed-in identity — the auth endpoint returns no profile, so the
      // submitted username (an email) is what identity consumers get.
      user: { email: input.username },
      accessToken: data.access_token ?? '',
    };
  } catch (err) {
    if (isDevelopment) {
      console.error('[authenticate]', err);
    }

    // Re-throw an already-mapped status code untouched; collapse everything
    // else (wrong credentials, transport failures, unknown codes) to
    // INCORRECT_LOGIN_CREDENTIALS.
    const message = err instanceof Error ? err.message : String(err);
    if ((Object.values(AuthMachineStatusCode) as string[]).includes(message)) {
      throw new Error(message);
    }

    throw new Error(AuthMachineStatusCode.INCORRECT_LOGIN_CREDENTIALS);
  }
});

export default authenticate;
